// eslint-disable-next-line no-unused-vars
import React, {useState, useEffect, useRef} from 'react';
import PropTypes from 'prop-types';
import Locale from '../util/Locale';
import Util from '../util/Util';
import './ImageRotate.scss';

function ImageRotate({setIsRotating = () => {}, src = null, onApply = () => {}}) {
    const [isFlipped, setIsFlipped] = useState(false);
    const sourceRef = useRef();
    const [imageSource] = useState(src);
    const [image] = useState(new Image());
    const [rotation, setRotation] = useState(0);
    const [hasOrientationChange, setHasOrientationChange] = useState(false);

    const drawSourceCanvas = (scaleArg, rotationArg, flipArg) => {
        const ctx = sourceRef.current.getContext('2d');
        const iw = image.naturalWidth * scaleArg;
        const ih = image.naturalHeight * scaleArg;

        if (rotationArg % 180 === 0) {
            sourceRef.current.width = iw;
            sourceRef.current.height = ih;
        } else {
            sourceRef.current.width = ih;
            sourceRef.current.height = iw;
        }

        ctx.save();
        ctx.translate(sourceRef.current.width / 2, sourceRef.current.height / 2);
        ctx.rotate((rotationArg * Math.PI) / 180);
        if (flipArg) {
            ctx.scale(-1, 1);
            ctx.drawImage(image, (-iw / 2) * -1, -ih / 2, -iw, ih);
        } else {
            ctx.drawImage(image, -iw / 2, -ih / 2, iw, ih);
        }
        ctx.restore();
    };

    useEffect(() => {
        if (src) {
            image.setAttribute('crossOrigin', '');
            image.src = imageSource;

            image.onload = () => {
                drawSourceCanvas(1, rotation, isFlipped);
            };
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (image.complete && image.naturalWidth) {
            drawSourceCanvas(1, rotation, isFlipped);
            setHasOrientationChange(rotation !== 0 || isFlipped);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rotation, isFlipped]);

    const handleFlipHorizontal = () => {
        setIsFlipped(prev => !prev);
    };

    const handleRotateLeft = () => {
        setRotation(prev => (prev - 90 + 360) % 360);
    };

    const handleRotateRight = () => {
        setRotation(prev => (prev + 90) % 360);
    };

    const renderCanvas = () => {
        return (
            <div className="outer-canvas">
                <div className="inner-canvas">
                    <canvas ref={sourceRef} />
                </div>
            </div>
        );
    };

    const processImageData = async () => {
        const url = await sourceRef.current.toDataURL();
        return {
            image: url,
            width: sourceRef.current.width,
            height: sourceRef.current.height,
            flipped: isFlipped
        };
    };

    const returnData = async () => {
        const data = await processImageData();
        onApply(data);
    };

    const renderMenu = () => {
        return (
            <div className="rotate-menu">
                <div className="control-block">
                    {!Util.isMobile() && (
                        <p className="rotation-text">{`${Locale.getText('ROTATION')}: ${rotation}° | Flipped: ${isFlipped ? 'Yes' : 'No'}`}</p>
                    )}
                    <nsemble-button
                        class="rotate-left"
                        text
                        onClick={handleRotateLeft}
                        title={Locale.getText('ROTATE_LEFT')}
                    >
                        <svg
                            fill="none"
                            height="18"
                            stroke="#00528A"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            viewBox="0 0 24 24"
                            width="18"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" />
                        </svg>
                        {!Util.isMobile() && Locale.getText('ROTATE_LEFT')}
                    </nsemble-button>
                    <nsemble-button
                        className="rotate-right"
                        text
                        onClick={handleRotateRight}
                        title={Locale.getText('ROTATE_RIGHT')}
                    >
                        <svg
                            fill="none"
                            height="18"
                            stroke="#00528A"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            viewBox="0 0 24 24"
                            width="18"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" />
                        </svg>
                        {!Util.isMobile() && Locale.getText('ROTATE_RIGHT')}
                    </nsemble-button>
                    <nsemble-button
                        className="flip-horizontal"
                        text
                        onClick={handleFlipHorizontal}
                        title={Locale.getText('FLIP_HORIZONTAL')}
                    >
                        <svg
                            fill="none"
                            height="18"
                            stroke="#00528A"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            viewBox="0 0 24 24"
                            width="18"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path d="M7.5 21 3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5" />
                        </svg>
                        {!Util.isMobile() && Locale.getText('FLIP_HORIZONTAL')}
                    </nsemble-button>
                </div>
                <div className="control-block">
                    <nsemble-button
                        text
                        onClick={() => {
                            setIsRotating(false);
                        }}
                        title={Locale.getText('CANCEL')}
                    >
                        {Locale.getText('CANCEL')}
                    </nsemble-button>
                    <nsemble-button
                        text
                        disabled={!hasOrientationChange ? true : undefined}
                        onClick={() => {
                            returnData();
                        }}
                        title={Locale.getText('APPLY')}
                    >
                        {Locale.getText('APPLY')}
                    </nsemble-button>
                </div>
            </div>
        );
    };

    return (
        <div className="asset-rotate">
            <div className="rotate-tool">
                {renderCanvas()}
                {renderMenu()}
            </div>
        </div>
    );
}

ImageRotate.propTypes = {
    setIsRotating: PropTypes.func,
    src: PropTypes.string,
    onApply: PropTypes.func
};

export default ImageRotate;
